import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, ApiError, isDevAuthMode, clearApiKey, setApiKey, getApiKey } from '@/api/client';

interface AuthUser {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  github_login: string | null;
  email: string | null;
  status: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isDevMode: boolean;
  logout: () => Promise<void>;
  refetchUser: () => Promise<void>;
  loginWithApiKey: (key: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    // In dev API key mode, validate key via dedicated dev-login endpoint
    if (isDevAuthMode()) {
      try {
        const data = await api.post<{ ok: boolean; user_id: string; key_name: string }>(
          '/api/v1/auth/dev-login',
          { api_key: getApiKey() },
        );
        setUser({
          id: data.user_id,
          display_name: `Dev User (${data.key_name})`,
          avatar_url: null,
          github_login: null,
          email: null,
          status: 'active',
        });
      } catch {
        clearApiKey();
        setUser(null);
      }
    } else {
      // Cookie auth mode — call /auth/me
      try {
        const data = await api.get<AuthUser>('/api/v1/auth/me');
        setUser(data);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setUser(null);
        } else {
          setUser(null);
        }
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const logout = useCallback(async () => {
    if (isDevAuthMode()) {
      clearApiKey();
    } else {
      try {
        await api.post('/api/v1/auth/logout');
      } catch {
        // Ignore errors on logout
      }
    }
    setUser(null);
  }, []);

  const refetchUser = useCallback(async () => {
    setIsLoading(true);
    await fetchUser();
  }, [fetchUser]);

  const loginWithApiKey = useCallback(async (key: string): Promise<boolean> => {
    setApiKey(key);
    try {
      const data = await api.post<{ ok: boolean; user_id: string; key_name: string }>(
        '/api/v1/auth/dev-login',
        { api_key: key },
      );
      setUser({
        id: data.user_id,
        display_name: `Dev User (${data.key_name})`,
        avatar_url: null,
        github_login: null,
        email: null,
        status: 'active',
      });
      return true;
    } catch {
      clearApiKey();
      return false;
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      isDevMode: isDevAuthMode(),
      logout,
      refetchUser,
      loginWithApiKey,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
